// DashboardHeader
// ---------------
// Top navigation bar: sidebar toggle, brand, search, notifications dropdown,
// condo switcher (super-admin only), and user profile dropdown.
//
// Keeps zero local state — everything is driven by props from MainDashboard.
import React from 'react';
import {
  Button, Space, Typography, Avatar, Badge, Dropdown, Select, Input, Card, List, Tag,
  type MenuProps,
} from 'antd';
import {
  UserOutlined, HomeOutlined, MenuFoldOutlined, MenuUnfoldOutlined,
  BellOutlined, DownOutlined, CheckCircleOutlined, WarningOutlined,
  BankOutlined,
} from '@ant-design/icons';

import type { Condo, Fee, Owner } from '../../types';

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

export interface DashboardHeaderProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;

  user: { name?: string; email?: string; role?: string } | null;
  userMenuItems: MenuProps['items'];
  userDropdownOpen: boolean;
  onUserDropdownOpenChange: (open: boolean) => void;

  overdueFees: Fee[];
  owners: Owner[];

  // Condo switcher (only shown for super_admin_condo)
  availableCondos: Condo[] | null | undefined;
  currentCondo: Condo | null | undefined;
  onCondoSwitch: (condoId: string) => void;
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  collapsed, onToggleCollapsed,
  user, userMenuItems, userDropdownOpen, onUserDropdownOpenChange,
  overdueFees, owners,
  availableCondos, currentCondo, onCondoSwitch,
}) => {
  const roleLabel = (user?.role === 'super_admin' || user?.role === 'superadmin' || user?.role === 'super_admin_condo')
    ? 'System Administrator'
    : user?.role === 'admin'
      ? 'Property Manager'
      : 'Resident';

  return (
    <div style={{
      background: 'linear-gradient(135deg, #1a365d 0%, #2d3748 100%)',
      padding: '0 28px',
      height: 72,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      position: 'sticky',
      top: 0,
      zIndex: 1000,
    }}>
      <Space size="middle" align="center">
        <Button
          type="text"
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={onToggleCollapsed}
          style={{ fontSize: '18px', width: 44, height: 44, color: '#fff', background: 'rgba(255,255,255,0.1)', borderRadius: 8 }}
        />

        <div style={{
          display: 'flex', alignItems: 'center',
          padding: '8px 16px',
          background: 'rgba(255,255,255,0.1)',
          borderRadius: 8,
          border: '1px solid rgba(255,255,255,0.2)',
        }}>
          <div style={{
            width: 36, height: 36,
            background: 'linear-gradient(135deg, #4299e1 0%, #3182ce 100%)',
            borderRadius: 8, marginRight: 12,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(66, 153, 225, 0.3)',
          }}>
            <HomeOutlined style={{ color: '#fff', fontSize: 18 }} />
          </div>
          {/* Sprint 27 — finding S2. Without `whiteSpace: 'nowrap'` the
              brand+tagline labels broke into one-letter-per-line ladders
              when the surrounding flex row got squeezed (visible on the
              dashboard pages at viewport widths <= ~1000px or when the
              sidebar toggle pushed the header past its natural width).
              `nowrap` keeps the brand atomic; `overflow: hidden` +
              `textOverflow: ellipsis` truncate gracefully on the rare
              ultra-narrow layouts instead of wrapping. */}
          <div style={{ minWidth: 0, overflow: 'hidden' }}>
            <Title level={5} style={{
              margin: 0, color: '#fff', fontWeight: 600, lineHeight: 1.2,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>CondoPro Management</Title>
            <Text style={{
              color: '#cbd5e0', fontSize: 12, display: 'block',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>Enterprise Condominium Platform</Text>
          </div>
        </div>
      </Space>

      <Space size="large" align="center">
        <Search
          placeholder="Search across properties..."
          style={{ width: 280, borderRadius: 8, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)' }}
          allowClear
          size="middle"
        />

        {/* Notifications: overdue fees dropdown */}
        <Dropdown
          menu={{
            items: [
              {
                key: 'notifications',
                label: (
                  <Card title="Notifications" size="small" style={{ width: 320, borderRadius: 8 }} extra={<Button type="link" size="small">Mark all read</Button>}>
                    <List
                      size="small"
                      dataSource={overdueFees.slice(0, 3)}
                      renderItem={(fee: Fee) => {
                        const owner = owners.find(o => o.id === fee.ownerId);
                        return (
                          <List.Item>
                            <List.Item.Meta
                              avatar={<WarningOutlined style={{ color: '#f56565' }} />}
                              title={`Overdue Payment - Unit ${owner?.unitNumber}`}
                              description={`${fee.description} - $${fee.amount}`}
                            />
                          </List.Item>
                        );
                      }}
                    />
                  </Card>
                ),
              },
            ],
          }}
          trigger={['click']}
          placement="bottomRight"
        >
          {/* Sprint 35 — finding G7. Was rendering the raw overdue-fee
              count next to the bell with no way to dismiss it ("11"
              became permanent visual noise on every Maple page).
              Until a real notifications store with read-markers
              exists, switch to a `dot` indicator: still surfaces
              "there's something to look at" but doesn't compete with
              the dashboard's own KPI cards for attention, and quietly
              disappears for condos / users with no overdue fees. */}
          <Badge dot={overdueFees.length > 0} status="error"
                 style={{ boxShadow: '0 0 0 2px #2d3748' }}>
            <Button
              type="text"
              icon={<BellOutlined />}
              style={{ fontSize: '18px', color: '#fff', width: 44, height: 44, background: 'rgba(255,255,255,0.1)', borderRadius: 8 }}
            />
          </Badge>
        </Dropdown>

        {/* Property switcher.
            Sprint 81 (BUG-5) — was gated to `super_admin_condo` only, which
            left an ADMIN with no bound property (condoId: null) stranded on
            the dashboard with "No property selected" and no way to pick one,
            so every management page rendered empty (it keys off currentCondo).
            Now any admin-tier user (admin OR super-admin) who has ≥1 available
            condo gets the switcher. Owners keep a single condo and don't need
            it. When the user has no condos at all, we show an honest empty
            state instead of a silent dead-end. */}
        {(() => {
          const isAdminTier =
            user?.role === 'super_admin_condo' ||
            user?.role === 'superadmin' ||
            user?.role === 'super_admin' ||
            user?.role === 'admin';
          if (!isAdminTier) return null;

          const condos = availableCondos ?? [];
          if (condos.length > 0) {
            return (
              <Select
                value={currentCondo?.id}
                onChange={onCondoSwitch}
                placeholder="Select property"
                style={{ width: 220, background: 'rgba(255,255,255,0.1)', borderRadius: 8 }}
                bordered={false}
                suffixIcon={<DownOutlined style={{ color: '#fff' }} />}
                dropdownStyle={{ borderRadius: 8 }}
              >
                {condos.map((condo: Condo) => (
                  <Option key={condo.id} value={condo.id}>
                    <Space>
                      <BankOutlined />
                      <span>{condo.name}</span>
                      {currentCondo?.id === condo.id && (<CheckCircleOutlined style={{ color: '#48bb78' }} />)}
                    </Space>
                  </Option>
                ))}
              </Select>
            );
          }

          // No properties assigned — surface it rather than silently
          // trapping the manager on an empty dashboard.
          return (
            <Tag color="warning" icon={<WarningOutlined />} style={{ margin: 0, padding: '4px 10px', borderRadius: 8 }}>
              No properties assigned
            </Tag>
          );
        })()}

        {/* User profile */}
        <Dropdown
          menu={{ items: userMenuItems }}
          placement="bottomRight"
          overlayStyle={{ borderRadius: 8, zIndex: 900 }}
          open={userDropdownOpen}
          onOpenChange={onUserDropdownOpenChange}
        >
          <Space style={{
            cursor: 'pointer',
            padding: '8px 12px',
            borderRadius: 8,
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            minWidth: 180,
          }}>
            <Avatar size="small" icon={<UserOutlined />} style={{ background: 'linear-gradient(135deg, #4299e1 0%, #3182ce 100%)' }} />
            <div style={{ textAlign: 'left' }}>
              <Text style={{ color: '#fff', fontWeight: 500, fontSize: 14, display: 'block', lineHeight: 1.2 }}>
                {user?.name || user?.email?.split('@')[0]}
              </Text>
              <Text style={{ color: '#cbd5e0', fontSize: 12, display: 'block', lineHeight: 1.2 }}>{roleLabel}</Text>
            </div>
          </Space>
        </Dropdown>
      </Space>
    </div>
  );
};
